import React, { useState, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { ExamConfig, CertificationType } from '../../types';
import { 
    Settings, Plus, Edit, Trash2, X, 
    MapPin, CheckCircle, Loader2,
    Sparkles, Wand2, Database, Download, Award, Globe, ShieldCheck,
    AlertTriangle, RefreshCw, Layers, Check, ArrowRight
} from 'lucide-react';
import { StorageService } from '../../services/storageService';
import { PageLoader } from '../../components/PageLoader';
import { generateExamConfig } from '../../services/geminiService';
import { useThemeTokens } from '../../hooks/useActiveTheme';

interface UIExamConfig extends ExamConfig {
  _id?: string;
  certId: string;
  stateCode: string;
  active: boolean;
  questionCount: number;
}

const getCertDefaults = (certId: string, certName?: string) => {
    const id = (certId || '').toLowerCase();
    const name = (certName || '').toLowerCase();

    if (id.includes('phlebotom') || name.includes('phlebotom') || id.includes('cpt')) {
        return { vendor: 'NHA / AMCA', timeLimitMinutes: 120, questionCount: 100, passingPercent: 78 };
    }
    if (id.includes('medical_assistant') || name.includes('medical assistant') || id.includes('cma') || id.includes('ccma')) {
        return { vendor: 'NHA (CCMA) / AAMA', timeLimitMinutes: 150, questionCount: 150, passingPercent: 75 };
    }
    if (id.includes('pharmacy') || name.includes('pharmacy') || id.includes('cpht')) {
        return { vendor: 'PTCB / PTCE', timeLimitMinutes: 120, questionCount: 90, passingPercent: 75 };
    }
    if (id.includes('ekg') || name.includes('ekg') || name.includes('cardi') || id.includes('cet')) {
        return { vendor: 'NHA (CET) / AMCA', timeLimitMinutes: 120, questionCount: 100, passingPercent: 75 };
    }
    if (id.includes('pct') || name.includes('patient care') || id.includes('cpct')) {
        return { vendor: 'NHA (CPCT) / AMCA', timeLimitMinutes: 120, questionCount: 100, passingPercent: 75 };
    }
    if (id.includes('cna') || name.includes('nursing assistant')) {
        return { vendor: 'Prometric', timeLimitMinutes: 120, questionCount: 70, passingPercent: 75 };
    }
    return {
        vendor: `${(certName || certId).toUpperCase()} National Board Standard`,
        timeLimitMinutes: 120,
        questionCount: 75,
        passingPercent: 75
    };
};

export const ExamConfigManager: React.FC = () => {
    const tokens = useThemeTokens();
    const location = useLocation();

    // Query params support for auto-selecting cert (e.g. from Cert Manager)
    const initialCertQuery = useMemo(() => {
        try {
            const params = new URLSearchParams(location.search);
            return params.get('certId') || '';
        } catch (e) {
            return '';
        }
    }, [location.search]);

    const [certId, setCertId] = useState(initialCertQuery || 'cna');
    const [certs, setCerts] = useState<CertificationType[]>([]);
    const [configs, setConfigs] = useState<UIExamConfig[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isEditing, setIsEditing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isGenerating, setIsGenerating] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
    const [currentConfig, setCurrentConfig] = useState<Partial<UIExamConfig> | null>(null);
    const [aiKeyword, setAiKeyword] = useState('');
    const [status, setStatus] = useState<{type: 'error' | 'success' | 'warning', text: string} | null>(null);
    const [isRepairingIndexes, setIsRepairingIndexes] = useState(false);

    // 1-Click 50 States Provisioning State
    const [isBulkModalOpen, setIsBulkModalOpen] = useState(false);
    const [isBulkGenerating, setIsBulkGenerating] = useState(false);
    const [bulkOverwrite, setBulkOverwrite] = useState(false);
    const [bulkVendor, setBulkVendor] = useState('');
    const [bulkTimeLimit, setBulkTimeLimit] = useState<number>(120);
    const [bulkQuestionCount, setBulkQuestionCount] = useState<number>(70);
    const [bulkPassingPercent, setBulkPassingPercent] = useState<number>(75);

    useEffect(() => {
        StorageService.getCertifications().then(data => {
            setCerts(data);
            if (data.length > 0) {
                if (initialCertQuery && data.some(c => c.id === initialCertQuery.toLowerCase())) {
                    setCertId(initialCertQuery.toLowerCase());
                } else {
                    setCertId(data[0].id);
                }
            }
        }).catch(err => {
            console.warn("Failed to load certifications in ExamConfigManager:", err);
        });
    }, [initialCertQuery]);

    const activeCert = certs.find(c => c.id === certId);

    const loadConfigs = async () => {
        setIsLoading(true);
        setStatus(null);
        try {
            const items = await StorageService.getExamConfigs(certId);
            setConfigs(items || []);
        } catch (e: any) {
            console.error("Failed to load exam configs:", e);
            setStatus({ type: 'error', text: e.message || 'Registry unavailable.' });
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => { if (certId) loadConfigs(); }, [certId]);

    const handleAdd = () => {
        const defaults = getCertDefaults(certId, activeCert?.name);
        setCurrentConfig({
            certId,
            stateCode: '',
            vendor: defaults.vendor,
            timeLimitMinutes: defaults.timeLimitMinutes,
            passingPercent: defaults.passingPercent,
            questionCount: defaults.questionCount,
            active: true
        });
        setAiKeyword('');
        setStatus(null);
        setIsEditing(true);
    };

    const handleEdit = (config: UIExamConfig) => {
        setCurrentConfig({ ...config });
        setAiKeyword('');
        setStatus(null);
        setIsEditing(true);
    };

    const handleFillWithAI = async () => {
        if (!aiKeyword.trim()) {
            setStatus({ type: 'error', text: 'Enter a state or country name keyword first.' });
            return;
        }
        setIsGenerating(true);
        setStatus(null);
        try {
            const certName = activeCert?.name || certId.toUpperCase();
            const aiResult = await generateExamConfig(aiKeyword, certName);
            setCurrentConfig(prev => ({
                ...(prev || {}),
                certId: certId,
                ...aiResult
            }));
            setStatus({ type: 'success', text: `Logic synthesized for ${aiResult.stateCode || aiKeyword} from board registry.` });
        } catch (e: any) {
            setStatus({ type: 'error', text: 'AI Synthesis failed: ' + e.message });
        } finally {
            setIsGenerating(false);
        }
    };

    const handleDelete = async (id: string) => {
        if (!confirm('Permanently delete jurisdictional protocol?')) return;
        try {
            await StorageService.deleteExamConfig(id);
            setStatus({ type: 'success', text: 'Jurisdictional protocol purged.' });
            loadConfigs();
        } catch (e: any) {
            setStatus({ type: 'error', text: e.message || 'Purge failed.' });
        }
    };

    const handleRepairIndexes = async () => {
        setIsRepairingIndexes(true);
        setStatus(null);
        try {
            const res = await StorageService.repairExamConfigIndexes();
            setStatus({
                type: 'success',
                text: res.droppedLegacy 
                    ? 'Registry schema repaired: Removed legacy single-field index constraints and verified compound multi-cert uniqueness.' 
                    : 'Registry schema verified: All jurisdictional compound indexes are optimal and active.'
            });
            await loadConfigs();
        } catch (e: any) {
            console.error("Index repair error:", e);
            setStatus({ type: 'error', text: e.message || 'Failed to sync registry schema.' });
        } finally {
            setIsRepairingIndexes(false);
        }
    };

    const handleSave = async () => {
        if (!currentConfig) return;
        const targetState = (currentConfig.stateCode || '').trim().toUpperCase();
        if (!targetState) {
            setStatus({ type: 'error', text: 'Jurisdiction state code is required (e.g. CA, FL, TX, or GLOBAL).' });
            return;
        }

        setIsSaving(true);
        setStatus(null);
        try {
            const targetCertId = (currentConfig.certId || certId).toLowerCase().trim();
            const defaults = getCertDefaults(targetCertId, activeCert?.name);

            const payload = {
                ...currentConfig,
                certId: targetCertId,
                stateCode: targetState,
                vendor: (currentConfig.vendor || defaults.vendor || 'Prometric').trim(),
                timeLimitMinutes: Number(currentConfig.timeLimitMinutes) || defaults.timeLimitMinutes || 120,
                questionCount: Number(currentConfig.questionCount) || defaults.questionCount || 70,
                passingPercent: Number(currentConfig.passingPercent) || defaults.passingPercent || 75,
                active: currentConfig.active !== undefined ? !!currentConfig.active : true
            };

            try {
                await StorageService.saveExamConfig(payload);
            } catch (saveErr: any) {
                // Self-healing: if legacy single-field index (stateCode_1) duplicate key error is encountered, repair schema and retry
                if (saveErr.message && (saveErr.message.includes('stateCode_1') || saveErr.message.includes('E11000') || saveErr.message.includes('duplicate key'))) {
                    console.warn("Detected legacy index conflict on exam state config save. Auto-repairing schema and retrying...");
                    setStatus({ type: 'warning', text: 'Detected legacy database index constraint. Synchronizing registry schema...' });
                    await StorageService.repairExamConfigIndexes();
                    await StorageService.saveExamConfig(payload);
                } else {
                    throw saveErr;
                }
            }

            setStatus({ type: 'success', text: `Protocol published for ${payload.stateCode} (${payload.vendor})` });
            setIsEditing(false);
            setCurrentConfig(null);
            loadConfigs();
        } catch (e: any) {
            console.error("Save exam protocol error:", e);
            setStatus({ type: 'error', text: e.message || 'Save protocol interrupted.' });
        } finally {
            setIsSaving(false);
        }
    };

    const openBulkModal = () => {
        const defaults = getCertDefaults(certId, activeCert?.name);
        setBulkVendor(defaults.vendor);
        setBulkTimeLimit(defaults.timeLimitMinutes);
        setBulkQuestionCount(defaults.questionCount);
        setBulkPassingPercent(defaults.passingPercent);
        setBulkOverwrite(false);
        setIsBulkModalOpen(true);
    };

    const handleBulkGenerate50States = async () => {
        setIsBulkGenerating(true);
        setStatus(null);
        try {
            const result = await StorageService.bulkGenerate50StatesExamConfigs({
                certId,
                overwriteExisting: bulkOverwrite,
                vendor: bulkVendor.trim() || undefined,
                timeLimitMinutes: Number(bulkTimeLimit) || undefined,
                questionCount: Number(bulkQuestionCount) || undefined,
                passingPercent: Number(bulkPassingPercent) || undefined
            });

            setStatus({
                type: 'success',
                text: result.message || `Successfully provisioned ${result.totalJurisdictions || 51} state exam protocols for ${activeCert?.shortName || certId.toUpperCase()}!`
            });
            setIsBulkModalOpen(false);
            await loadConfigs();
        } catch (e: any) {
            console.error("Bulk generate 50 states error:", e);
            setStatus({ type: 'error', text: e.message || '1-Click 50 States provisioning failed.' });
        } finally {
            setIsBulkGenerating(false);
        }
    };

    const handleExport = () => {
        setIsExporting(true);
        try {
            const dataStr = JSON.stringify(configs, null, 2);
            const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
            const fileName = `${certId}_exam_protocols_${new Date().toISOString().split('T')[0]}.json`;
            const linkElement = document.createElement('a');
            linkElement.setAttribute('href', dataUri);
            linkElement.setAttribute('download', fileName);
            linkElement.click();
        } catch (e) {
            console.error(e);
        } finally {
            setIsExporting(false);
        }
    };

    return (
        <div className={`animate-fade-in space-y-8 pb-12 ${tokens.headerFont}`}>
            {/* Header Banner */}
            <div className={`flex flex-col lg:flex-row justify-between items-stretch gap-6 ${tokens.heroBannerBg} p-8 rounded-[2.5rem] shadow-xl text-white overflow-hidden relative`}>
                <div className="absolute top-0 right-0 w-96 h-96 bg-indigo-500/10 blur-[120px] rounded-full -translate-y-1/2 translate-x-1/2 pointer-events-none"></div>
                <div className="relative z-10 flex-1">
                    <div className="flex items-center gap-3 mb-6">
                        <div className={`p-3 ${tokens.iconBg} rounded-2xl shadow-xl shadow-indigo-900/20`}>
                            <Settings size={28} className="text-white" />
                        </div>
                        <div>
                            <h2 className="text-3xl font-black tracking-tight">Board Registry</h2>
                            <p className="text-[10px] font-black text-indigo-400 tracking-[0.3em] uppercase">Protocol Management</p>
                        </div>
                    </div>
                    <p className="text-slate-400 font-medium text-sm md:text-base max-w-2xl opacity-95 leading-relaxed">
                        Configure jurisdictional exam parameters for each healthcare certification. Ingest official board requirements across all 50 states in 1-click or customize individual state board standards.
                    </p>
                    
                    <div className="mt-8 flex flex-wrap items-center gap-3">
                        {/* Cert Selector */}
                        <div className="bg-white/10 p-1 rounded-2xl border border-white/15 flex items-center shrink-0">
                            <Award className="ml-3 text-indigo-400" size={16} />
                            <select 
                                value={certId} 
                                onChange={e => setCertId(e.target.value)} 
                                className="bg-transparent text-white px-4 py-2.5 rounded-xl outline-none font-black text-xs cursor-pointer"
                            >
                                {certs.map(c => (
                                    <option key={c.id} value={c.id} className="text-slate-900 font-bold">
                                        {c.name} ({c.shortName || c.id.toUpperCase()})
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* One-Click 50 States Button */}
                        <button 
                            onClick={openBulkModal}
                            className="px-6 py-3.5 bg-emerald-600 hover:bg-emerald-500 text-white rounded-2xl font-black text-xs tracking-wider uppercase flex items-center gap-2 shadow-2xl transition-all active:scale-95 border-b-4 border-emerald-800 cursor-pointer"
                            title="Generate exam parameters for all 50 states + Universal Standard in 1 click"
                        >
                            <Sparkles size={16} className="text-amber-300" />
                            <span>1-Click 50 States Ingest</span>
                        </button>

                        {/* Add Single Protocol */}
                        <button 
                            onClick={handleAdd}
                            className={`px-6 py-3.5 ${tokens.primaryBtn} rounded-2xl font-black text-xs tracking-wider uppercase flex items-center gap-2 shadow-xl transition-all active:scale-95 border-b-4 border-indigo-800 cursor-pointer`}
                        >
                            <Plus size={16} />
                            <span>New Protocol</span>
                        </button>

                        {/* Backup Registry */}
                        <button 
                            onClick={handleExport}
                            disabled={configs.length === 0 || isExporting}
                            className="px-5 py-3.5 bg-white/5 text-white rounded-2xl font-black text-xs tracking-wider uppercase flex items-center gap-2 hover:bg-white/10 transition-all border border-white/10 disabled:opacity-40 cursor-pointer"
                        >
                            {isExporting ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
                            <span>Export ({configs.length})</span>
                        </button>

                        {/* Sync Registry Indexes / Schema Repair */}
                        <button 
                            onClick={handleRepairIndexes}
                            disabled={isRepairingIndexes}
                            className="px-5 py-3.5 bg-white/5 text-white rounded-2xl font-black text-xs tracking-wider uppercase flex items-center gap-2 hover:bg-white/10 transition-all border border-white/10 disabled:opacity-40 cursor-pointer"
                            title="Repair database schema indexes and drop legacy single-field duplicate constraints"
                        >
                            <RefreshCw size={16} className={isRepairingIndexes ? "animate-spin text-indigo-400" : "text-indigo-400"} />
                            <span>{isRepairingIndexes ? "Syncing..." : "Sync Indexes"}</span>
                        </button>
                    </div>
                </div>
            </div>

            {/* Status Notification Banner */}
            {status && !isEditing && !isBulkModalOpen && (
                <div className={`p-4 rounded-3xl flex items-center justify-between animate-pop border-2 shadow-xl ${
                    status.type === 'error' 
                        ? 'bg-rose-50 border-rose-200 text-rose-700' 
                        : status.type === 'warning'
                        ? 'bg-amber-50 border-amber-200 text-amber-800'
                        : 'bg-emerald-50 border-emerald-200 text-emerald-700'
                }`}>
                    <div className="flex items-center gap-3 ml-2">
                        {status.type === 'error' ? (
                            <AlertTriangle size={20} className="text-rose-600 shrink-0"/>
                        ) : status.type === 'warning' ? (
                            <RefreshCw size={20} className="text-amber-600 shrink-0 animate-spin"/>
                        ) : (
                            <CheckCircle size={20} className="text-emerald-600 shrink-0"/>
                        )}
                        <span className="font-bold text-xs leading-relaxed">{status.text}</span>
                    </div>
                    <button onClick={() => setStatus(null)} className="p-2 hover:bg-black/5 rounded-xl transition-all cursor-pointer">
                        <X size={16}/>
                    </button>
                </div>
            )}

            {/* Content Body */}
            {isLoading ? (
                <PageLoader message={`Indexing registry protocols for ${activeCert?.shortName || certId.toUpperCase()}...`} />
            ) : configs.length === 0 ? (
                <div className="bg-white rounded-[3rem] p-12 md:p-20 text-center border-4 border-dashed border-slate-200 flex flex-col items-center gap-6 shadow-sm">
                    <div className="w-20 h-20 bg-indigo-50 text-indigo-600 rounded-3xl flex items-center justify-center shadow-inner">
                        <Sparkles size={40} className="text-indigo-600 animate-pulse" />
                    </div>
                    <div className="space-y-2 max-w-lg">
                        <p className="text-2xl font-black text-slate-900 tracking-tight">
                            No Protocols Configured for {activeCert?.name || activeCert?.shortName || certId.toUpperCase()}
                        </p>
                        <p className="text-sm font-medium text-slate-500 leading-relaxed">
                            Generate official exam board parameters across all 50 states plus the National Universal Standard with our automated clinical council pipeline.
                        </p>
                    </div>
                    <div className="flex flex-wrap items-center justify-center gap-4 pt-2">
                        <button
                            onClick={openBulkModal}
                            className="px-8 py-4 bg-emerald-600 hover:bg-emerald-500 text-white rounded-2xl font-black text-xs uppercase tracking-widest flex items-center gap-2.5 shadow-xl hover:shadow-2xl transition-all active:scale-95 border-b-4 border-emerald-800 cursor-pointer"
                        >
                            <Sparkles size={18} className="text-amber-300" />
                            <span>Generate All 50 States (1-Click)</span>
                        </button>
                        <button
                            onClick={handleAdd}
                            className="px-6 py-4 bg-slate-100 hover:bg-slate-200 text-slate-800 rounded-2xl font-black text-xs uppercase tracking-widest flex items-center gap-2 transition-all border border-slate-300 cursor-pointer"
                        >
                            <Plus size={18} />
                            <span>Add Single Protocol</span>
                        </button>
                    </div>
                </div>
            ) : (
                <div className="space-y-6">
                    <div className="flex justify-between items-center px-2">
                        <div className="flex items-center gap-3">
                            <span className="px-3 py-1 bg-indigo-50 border border-indigo-200 text-indigo-700 rounded-xl font-black text-xs uppercase tracking-widest">
                                {configs.length} Jurisdictions Configured
                            </span>
                            <span className="text-xs text-slate-500 font-bold">
                                Target: {activeCert?.name || certId.toUpperCase()}
                            </span>
                        </div>
                        <button
                            onClick={openBulkModal}
                            className="text-xs font-black text-emerald-700 hover:text-emerald-800 flex items-center gap-1.5 uppercase tracking-wider transition-colors cursor-pointer"
                        >
                            <RefreshCw size={13} />
                            <span>Sync / Regenerate 50 States</span>
                        </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {configs.map(config => (
                            <div key={config._id || `${config.certId}_${config.stateCode}`} className={`bg-white rounded-[2.5rem] p-7 border-2 transition-all hover:shadow-xl flex flex-col justify-between space-y-6 ${config.active ? 'border-slate-200 shadow-xs' : 'border-slate-100 opacity-60 grayscale'}`}>
                                <div className="space-y-4">
                                    <div className="flex justify-between items-start">
                                        <div className="flex items-center gap-3.5">
                                            <div className={`w-14 h-14 rounded-2xl flex items-center justify-center text-lg font-black shadow-md ${config.active ? 'bg-slate-900 text-white' : 'bg-slate-200 text-slate-500'}`}>
                                                {config.stateCode}
                                            </div>
                                            <div>
                                                <h3 className="font-black text-slate-900 text-sm tracking-tight">{config.vendor}</h3>
                                                <div className="flex items-center gap-2 mt-1">
                                                    <span className={`text-[8px] font-black px-2 py-0.5 rounded-md ${config.active ? 'bg-emerald-50 text-emerald-600 border border-emerald-100' : 'bg-rose-50 text-rose-600 border border-rose-100'}`}>
                                                        {config.active ? 'Operational' : 'Paused'}
                                                    </span>
                                                    <span className="text-[8px] font-mono text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded">
                                                        {config.certId.toUpperCase()}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="flex gap-1.5">
                                            <button onClick={() => handleEdit(config)} className="p-2 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-xl transition-all cursor-pointer" title="Edit protocol">
                                                <Edit size={16} />
                                            </button>
                                            <button onClick={() => handleDelete(config._id!)} className="p-2 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-xl transition-all cursor-pointer" title="Delete protocol">
                                                <Trash2 size={16} />
                                            </button>
                                        </div>
                                    </div>

                                    {/* Stats Grid */}
                                    <div className="grid grid-cols-3 gap-2 p-3 bg-slate-50 rounded-2xl border border-slate-100 text-center">
                                        <div>
                                            <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Time Limit</p>
                                            <p className="text-xs font-black text-slate-800 mt-0.5">{config.timeLimitMinutes}m</p>
                                        </div>
                                        <div className="border-x border-slate-200/80">
                                            <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Items</p>
                                            <p className="text-xs font-black text-slate-800 mt-0.5">{config.questionCount} Qs</p>
                                        </div>
                                        <div>
                                            <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Passing</p>
                                            <p className="text-xs font-black text-emerald-600 mt-0.5">{config.passingPercent}%</p>
                                        </div>
                                    </div>
                                </div>

                                <div className="pt-2 border-t border-slate-100 flex items-center justify-between text-[10px] text-slate-400 font-bold">
                                    <span>Board Verified</span>
                                    <span className="flex items-center gap-1 text-indigo-600">
                                        <ShieldCheck size={12} /> Live Registry
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* 1-Click 50 States Provisioning Modal */}
            {isBulkModalOpen && (
                <div className="fixed inset-0 z-[250] bg-slate-950/80 backdrop-blur-md flex items-center justify-center p-4 overflow-y-auto animate-fade-in">
                    <div className="bg-white rounded-[3rem] shadow-2xl max-w-xl w-full p-8 md:p-10 text-left space-y-8 animate-pop border-b-8 border-emerald-600">
                        <div className="flex justify-between items-start">
                            <div className="flex items-center gap-3.5">
                                <div className="p-3 bg-emerald-50 rounded-2xl text-emerald-600 border border-emerald-200">
                                    <Sparkles size={28} className="text-emerald-600" />
                                </div>
                                <div>
                                    <h3 className="text-2xl font-black text-slate-900 tracking-tight">1-Click 50-State Setup</h3>
                                    <p className="text-[10px] font-black text-emerald-600 tracking-widest uppercase mt-0.5">
                                        Automated Jurisdictional Ingestion
                                    </p>
                                </div>
                            </div>
                            <button 
                                onClick={() => setIsBulkModalOpen(false)}
                                className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-xl transition-all cursor-pointer"
                            >
                                <X size={20} />
                            </button>
                        </div>

                        <div className="p-4 bg-emerald-50/70 border border-emerald-200/80 rounded-2xl text-xs text-slate-700 leading-relaxed space-y-1.5">
                            <p className="font-bold text-emerald-950 flex items-center gap-1.5">
                                <Layers size={14} className="text-emerald-700" />
                                Target Certification: {activeCert?.name || certId.toUpperCase()} ({activeCert?.shortName || certId.toUpperCase()})
                            </p>
                            <p className="text-[11px] text-slate-600">
                                Automatically provisions parameters for all 50 states plus the Universal National Standard. State-specific board authorities (e.g. California CDPH/LFS, Prometric, Credentia, Headmaster) are mapped based on authoritative board standards.
                            </p>
                        </div>

                        {/* Defaults Configuration */}
                        <div className="space-y-5">
                            <div className="space-y-1.5">
                                <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Default Board / Agency</label>
                                <input 
                                    type="text" 
                                    value={bulkVendor} 
                                    onChange={e => setBulkVendor(e.target.value)}
                                    placeholder="e.g. NHA / AMCA, PTCB / PTCE, Prometric..."
                                    className="w-full px-4 py-3.5 bg-slate-50 border border-slate-200 rounded-2xl outline-none font-bold text-sm focus:bg-white focus:border-indigo-500 transition-all text-slate-900"
                                />
                            </div>

                            <div className="grid grid-cols-3 gap-3">
                                <div className="space-y-1.5">
                                    <label className="text-[9px] font-black text-slate-500 uppercase tracking-widest">Time (Min)</label>
                                    <input 
                                        type="number" 
                                        min="30" max="240" step="5"
                                        value={bulkTimeLimit} 
                                        onChange={e => setBulkTimeLimit(Number(e.target.value))}
                                        className="w-full px-3 py-3 bg-slate-50 border border-slate-200 rounded-2xl outline-none font-black text-sm text-center focus:bg-white text-slate-900"
                                    />
                                </div>
                                <div className="space-y-1.5">
                                    <label className="text-[9px] font-black text-slate-500 uppercase tracking-widest">Question Count</label>
                                    <input 
                                        type="number" 
                                        min="10" max="250" step="5"
                                        value={bulkQuestionCount} 
                                        onChange={e => setBulkQuestionCount(Number(e.target.value))}
                                        className="w-full px-3 py-3 bg-slate-50 border border-slate-200 rounded-2xl outline-none font-black text-sm text-center focus:bg-white text-slate-900"
                                    />
                                </div>
                                <div className="space-y-1.5">
                                    <label className="text-[9px] font-black text-slate-500 uppercase tracking-widest">Passing %</label>
                                    <input 
                                        type="number" 
                                        min="50" max="100" step="1"
                                        value={bulkPassingPercent} 
                                        onChange={e => setBulkPassingPercent(Number(e.target.value))}
                                        className="w-full px-3 py-3 bg-slate-50 border border-slate-200 rounded-2xl outline-none font-black text-sm text-center focus:bg-white text-slate-900"
                                    />
                                </div>
                            </div>

                            {/* Overwrite Checkbox */}
                            <label className="flex items-start gap-3 p-3.5 bg-slate-50 hover:bg-slate-100 rounded-2xl border border-slate-200 cursor-pointer transition-colors">
                                <input 
                                    type="checkbox" 
                                    checked={bulkOverwrite}
                                    onChange={e => setBulkOverwrite(e.target.checked)}
                                    className="mt-0.5 w-4 h-4 rounded text-emerald-600 focus:ring-emerald-500"
                                />
                                <div className="text-xs">
                                    <span className="font-bold text-slate-800">Overwrite existing state protocols</span>
                                    <p className="text-[10px] text-slate-500 mt-0.5">
                                        If unchecked, only missing states will be generated without changing custom edits on previously saved states.
                                    </p>
                                </div>
                            </label>
                        </div>

                        {/* Modal Action Buttons */}
                        <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-100">
                            <button
                                onClick={() => setIsBulkModalOpen(false)}
                                className="px-6 py-3.5 text-xs font-bold text-slate-500 hover:text-slate-800 transition-colors cursor-pointer"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleBulkGenerate50States}
                                disabled={isBulkGenerating}
                                className="px-8 py-3.5 bg-emerald-600 hover:bg-emerald-500 text-white rounded-2xl font-black text-xs uppercase tracking-widest flex items-center gap-2 shadow-xl hover:shadow-2xl transition-all active:scale-95 border-b-4 border-emerald-800 disabled:opacity-50 cursor-pointer"
                            >
                                {isBulkGenerating ? (
                                    <>
                                        <Loader2 size={16} className="animate-spin" />
                                        <span>Synthesizing 50 States...</span>
                                    </>
                                ) : (
                                    <>
                                        <Sparkles size={16} className="text-amber-300" />
                                        <span>Generate 51 State Protocols</span>
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Single Protocol Add/Edit Modal */}
            {isEditing && currentConfig && (
                <div className="fixed inset-0 z-[200] bg-slate-950/80 backdrop-blur-xl flex items-center justify-center p-4 overflow-y-auto animate-fade-in">
                    <div className="bg-white rounded-[3rem] shadow-2xl w-full max-w-4xl max-h-[92vh] flex flex-col animate-pop overflow-hidden border border-slate-200">
                        {/* Modal Header */}
                        <div className="p-8 border-b border-slate-100 flex justify-between items-center bg-slate-50/50 shrink-0">
                            <div className="flex items-center gap-3">
                                <div className="p-3 bg-indigo-50 text-indigo-600 rounded-2xl">
                                    <Settings size={22} />
                                </div>
                                <div>
                                    <h3 className="text-2xl font-black text-slate-900 tracking-tight">
                                        {currentConfig._id ? 'Edit Jurisdictional Protocol' : `Configure Jurisdictional Protocol (${activeCert?.shortName || certId.toUpperCase()})`}
                                    </h3>
                                    <p className="text-[10px] font-black text-indigo-600 tracking-widest uppercase">
                                        Target: {activeCert?.name || certId.toUpperCase()}
                                    </p>
                                </div>
                            </div>
                            <button 
                                onClick={() => setIsEditing(false)} 
                                className="p-2 text-slate-400 hover:text-slate-900 hover:bg-slate-200 rounded-xl transition-all cursor-pointer"
                            >
                                <X size={20} />
                            </button>
                        </div>

                        {/* Modal Body */}
                        <div className="p-8 space-y-8 overflow-y-auto flex-1 custom-scrollbar">
                            {/* AI Synthesis Box */}
                            <div className="bg-slate-900 p-8 rounded-[2.5rem] text-white relative overflow-hidden shadow-xl">
                                <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-500/10 blur-[80px] rounded-full pointer-events-none"></div>
                                <div className="relative z-10 space-y-4">
                                    <div className="flex items-center gap-3">
                                        <div className="p-2 bg-indigo-600 rounded-xl"><Wand2 size={18} className="text-white"/></div>
                                        <h4 className="text-base font-black tracking-tight">AI Protocol Ingestion</h4>
                                    </div>
                                    <div className="flex flex-col md:flex-row gap-3">
                                        <div className="relative flex-1 group">
                                            <Globe className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-indigo-400 transition-colors" size={18} />
                                            <input 
                                                type="text" 
                                                placeholder="Enter State Name (e.g. California, Florida, Texas, or Universal)..." 
                                                value={aiKeyword}
                                                onChange={e => setAiKeyword(e.target.value)}
                                                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleFillWithAI(); } }}
                                                className="w-full pl-11 pr-4 py-3.5 bg-white/10 border border-white/15 rounded-2xl outline-none font-bold text-sm focus:bg-white/15 transition-all text-white placeholder:text-slate-400"
                                            />
                                        </div>
                                        <button 
                                            onClick={handleFillWithAI}
                                            disabled={isGenerating || !aiKeyword.trim()}
                                            className="px-8 py-3.5 bg-indigo-600 text-white rounded-2xl font-black text-xs uppercase tracking-widest shadow-xl flex items-center justify-center gap-2 hover:bg-indigo-500 transition-all disabled:opacity-50 border-b-4 border-indigo-800 active:scale-95 cursor-pointer"
                                        >
                                            {isGenerating ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />} 
                                            <span>Synthesize with AI</span>
                                        </button>
                                    </div>
                                    <p className="text-[10px] font-semibold text-slate-400">
                                        Ingests verified testing vendor, duration, item count, and passing threshold for {activeCert?.name || 'this certification'} from board databases.
                                    </p>
                                </div>
                            </div>

                            {/* Form Grid */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                <div className="space-y-6">
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest flex items-center gap-1.5">
                                            <MapPin size={12}/> Jurisdictional Code
                                        </label>
                                        <input 
                                            type="text" 
                                            maxLength={10}
                                            value={currentConfig.stateCode || ''}
                                            onChange={(e) => setCurrentConfig({ ...currentConfig, stateCode: e.target.value.toUpperCase() })}
                                            className="w-full p-4 bg-slate-50 border border-slate-200 rounded-2xl outline-none font-black text-2xl uppercase focus:bg-white focus:border-indigo-500 transition-all text-slate-900"
                                            placeholder="CA, FL, TX, or GLOBAL"
                                        />
                                        <p className="text-[9px] text-slate-400 font-medium">Use 2-letter state code (e.g. CA, FL) or GLOBAL for universal national exams.</p>
                                    </div>

                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest flex items-center gap-1.5">
                                            <Database size={12}/> Testing Agency / Vendor
                                        </label>
                                        <input 
                                            type="text" 
                                            value={currentConfig.vendor || ''}
                                            onChange={(e) => setCurrentConfig({ ...currentConfig, vendor: e.target.value })}
                                            className="w-full p-4 bg-slate-50 border border-slate-200 rounded-2xl outline-none font-bold text-sm focus:bg-white focus:border-indigo-500 transition-all text-slate-900"
                                            placeholder="e.g. NHA / AMCA, Prometric, Credentia, Headmaster..."
                                        />
                                    </div>
                                </div>

                                <div className="space-y-6 bg-slate-50 p-6 rounded-[2.5rem] border border-slate-200/80">
                                    <div className="space-y-3">
                                        <div className="flex justify-between items-center px-1">
                                            <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Time Limit (Minutes)</label>
                                            <span className="bg-indigo-50 text-indigo-700 px-3 py-1 rounded-lg font-black text-xs border border-indigo-200">
                                                {currentConfig.timeLimitMinutes || 120}m
                                            </span>
                                        </div>
                                        <input 
                                            type="range" min="30" max="240" step="15"
                                            value={currentConfig.timeLimitMinutes || 120}
                                            onChange={(e) => setCurrentConfig({ ...currentConfig, timeLimitMinutes: parseInt(e.target.value) })}
                                            className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-indigo-600"
                                        />
                                    </div>

                                    <div className="space-y-3">
                                        <div className="flex justify-between items-center px-1">
                                            <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Item Registry Depth (Questions)</label>
                                            <span className="bg-medical-50 text-medical-700 px-3 py-1 rounded-lg font-black text-xs border border-medical-200">
                                                {currentConfig.questionCount || 70} Questions
                                            </span>
                                        </div>
                                        <input 
                                            type="range" min="10" max="200" step="5"
                                            value={currentConfig.questionCount || 70}
                                            onChange={(e) => setCurrentConfig({ ...currentConfig, questionCount: parseInt(e.target.value) })}
                                            className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-medical-500"
                                        />
                                    </div>

                                    <div className="space-y-3">
                                        <div className="flex justify-between items-center px-1">
                                            <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Passing Threshold (%)</label>
                                            <span className="bg-emerald-50 text-emerald-700 px-3 py-1 rounded-lg font-black text-xs border border-emerald-200">
                                                {currentConfig.passingPercent || 75}%
                                            </span>
                                        </div>
                                        <input 
                                            type="range" min="50" max="100" step="1"
                                            value={currentConfig.passingPercent || 75}
                                            onChange={(e) => setCurrentConfig({ ...currentConfig, passingPercent: parseInt(e.target.value) })}
                                            className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-emerald-500"
                                        />
                                    </div>

                                    <div className="flex items-center justify-between p-3.5 bg-white rounded-2xl shadow-xs border border-slate-200">
                                        <div>
                                            <p className="text-xs font-black text-slate-800">Operational Status</p>
                                            <p className={`text-[9px] font-black uppercase ${currentConfig.active ? 'text-emerald-600' : 'text-slate-400'}`}>
                                                {currentConfig.active ? 'ACTIVE IN HUB' : 'PROTOCOL PAUSED'}
                                            </p>
                                        </div>
                                        <button 
                                            type="button"
                                            onClick={() => setCurrentConfig({...currentConfig, active: !currentConfig.active})} 
                                            className={`transition-all cursor-pointer ${currentConfig.active ? 'text-indigo-600' : 'text-slate-300'}`}
                                        >
                                            {currentConfig.active ? <ToggleRight size={44} /> : <ToggleLeft size={44} />}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Inline Status Banner */}
                        {status && (
                            <div className={`mx-8 mb-4 p-4 rounded-2xl flex items-center justify-between border-2 shrink-0 ${
                                status.type === 'error' 
                                    ? 'bg-rose-50 border-rose-200 text-rose-700' 
                                    : status.type === 'warning'
                                    ? 'bg-amber-50 border-amber-200 text-amber-800'
                                    : 'bg-emerald-50 border-emerald-200 text-emerald-700'
                            }`}>
                                <div className="flex items-center gap-3">
                                    {status.type === 'error' ? (
                                        <AlertTriangle size={18} className="text-rose-600 shrink-0"/>
                                    ) : status.type === 'warning' ? (
                                        <RefreshCw size={18} className="text-amber-600 shrink-0 animate-spin"/>
                                    ) : (
                                        <CheckCircle size={18} className="text-emerald-600 shrink-0"/>
                                    )}
                                    <span className="font-bold text-xs">{status.text}</span>
                                </div>
                                <button onClick={() => setStatus(null)} className="p-1 hover:bg-black/5 rounded-lg cursor-pointer">
                                    <X size={14}/>
                                </button>
                            </div>
                        )}

                        {/* Modal Footer */}
                        <div className="p-6 border-t border-slate-100 bg-slate-50/50 flex justify-end gap-3 shrink-0">
                            <button 
                                onClick={() => setIsEditing(false)} 
                                className="px-8 py-3.5 font-bold text-xs text-slate-500 hover:text-slate-900 transition-colors cursor-pointer"
                            >
                                Discard
                            </button>
                            <button 
                                onClick={handleSave} 
                                disabled={isSaving || !currentConfig.stateCode}
                                className="px-10 py-3.5 bg-slate-900 hover:bg-slate-800 text-white rounded-2xl font-black text-xs tracking-wider uppercase shadow-xl transition-all flex items-center gap-2.5 disabled:opacity-50 border-b-4 border-black cursor-pointer active:scale-95"
                            >
                                {isSaving ? <Loader2 className="animate-spin" size={16}/> : <ShieldCheck size={16} />} 
                                <span>Save Protocol</span>
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

const ToggleLeft = ({size}: {size: number}) => (
    <svg width={size} height={size/2} viewBox="0 0 48 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect width="48" height="24" rx="12" fill="currentColor" fillOpacity="0.2"/>
        <circle cx="12" cy="12" r="8" fill="white"/>
    </svg>
);

const ToggleRight = ({size}: {size: number}) => (
    <svg width={size} height={size/2} viewBox="0 0 48 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect width="48" height="24" rx="12" fill="currentColor"/>
        <circle cx="36" cy="12" r="8" fill="white"/>
    </svg>
);
